<template>
  <div class="calendar-container">
    <!-- Controles del calendario -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <button class="btn calendar-nav-btn" @click="previousMonth">
        <i class="material-icons">chevron_left</i>
      </button>
      <h3 class="mb-0 calendar-title">{{ currentMonthName }} {{ currentYear }}</h3>
      <button class="btn calendar-nav-btn" @click="nextMonth">
        <i class="material-icons">chevron_right</i>
      </button>
    </div>

    <!--
      Referencia de los colores que hay en el mes. Solo lista los que realmente
      aparecen: el color es el estado de cada operación, no del campo de fecha, así
      que un mismo campo trae varios colores y hace falta poder leerlos.
    -->
    <div v-if="monthLegend.length > 0" class="calendar-legend mb-3">
      <span
        v-for="entry in monthLegend"
        :key="entry.key"
        class="calendar-legend-item"
      >
        <span
          class="calendar-legend-dot"
          :style="{ backgroundColor: entry.color }"
        ></span>
        {{ entry.label }}
        <span class="calendar-legend-count">{{ entry.count }}</span>
      </span>
    </div>

    <!-- Vista de escritorio -->
    <div v-if="!isMobile" class="desktop-calendar">
      <!-- Días de la semana -->
      <div class="calendar-header">
        <div v-for="day in weekDays" :key="day" class="calendar-day-header">
          {{ day }}
        </div>
      </div>

      <!-- Días del mes -->
      <div class="calendar-body">
        <div
          v-for="(week, weekIndex) in calendarDays"
          :key="weekIndex"
          class="calendar-week"
        >
          <div
            v-for="day in week"
            :key="day.date"
            class="calendar-day"
            :class="{
              'other-month': !day.currentMonth,
              'has-event': hasEventOnDate(day.dayKey),
              today: day.isToday,
              selected: isSelectedDate(day.dayKey),
            }"
          >
            <!-- El panel del día se abre desde el número; el chip va directo al ítem. -->
            <span
              class="day-number"
              role="button"
              @click="onDayClick(day)"
            >{{ day.dayNumber }}</span>
            <div v-if="hasEventOnDate(day.dayKey)" class="event-content">
              <div
                v-for="event in getEventsForDate(day.dayKey)"
                :key="`${event.modelName}-${event.fieldKey}-${event._id}`"
                class="event-name"
                :class="getEventStatusClass(event)"
                :style="getEventChipStyle(event)"
                :title="getEventTooltip(event)"
                @click="openEventItem(event, $event)"
              >
                <!--
                  El nombre de la operación va primero porque es el dato que se
                  busca; el campo queda en un código corto a la derecha. Antes era
                  «Loading date: OP…» y el ellipsis se comía justo el número.
                -->
                <span class="event-operation">{{ getEventOperationText(event) }}</span>
                <span class="event-field-code">{{ $t(event.fieldShort || event.field) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vista móvil -->
    <div v-else class="mobile-calendar">
      <div class="mobile-events-list">
        <div
          v-for="day in mobileCalendarDays"
          :key="day.date"
          class="mobile-day-card"
          :class="{
            'other-month': !day.currentMonth,
            today: day.isToday,
          }"
          @click="onMobileDayClick(day)"
        >
          <div class="mobile-day-header">
            <div class="mobile-day-info">
              <span class="mobile-day-number">{{ day.dayNumber }}</span>
              <span class="mobile-day-name">{{ getDayName(day.date) }}</span>
            </div>
            <div v-if="hasEventOnDate(day.dayKey)" class="mobile-event-count">
              {{ getEventsForDate(day.dayKey).length }} evento(s)
            </div>
          </div>

          <div v-if="hasEventOnDate(day.dayKey)" class="mobile-events">
            <div
              v-for="event in getEventsForDate(day.dayKey)"
              :key="`${event.modelName}-${event.fieldKey}-${event._id}`"
              class="mobile-event-item"
              :class="getEventStatusClass(event)"
              :style="getMobileEventStyle(event)"
              :title="getEventTooltip(event)"
              @click.stop="onMobileEventClick(event, day)"
            >
              <div class="mobile-event-content">
                <div class="mobile-event-field">{{ $t(event.field) }}</div>
                <div class="mobile-event-name">{{ event.title || event.item.title || event.item.name }}</div>
              </div>
              <div class="mobile-event-arrow">
                <i class="material-icons">chevron_right</i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/components/mobile/mobileTokens.css";
import {
  getShippingStatus,
  getShippingStatusClass,
  getShippingStatusColors,
  getShippingStatusLabel,
  getStatusColors,
  getStatusLabel,
  isShippingClientPendingSettled,
  SHIPPING_COLLECTED_CLASS,
} from '@/utils/shippingStatus.js';
import { toLocalYyyyMmDd, toYyyyMmDdSync } from '@/utils/dateFormatter.js';

/**
 * Orden de las entradas de la leyenda que no son estados de embarque.
 *
 * Los estados usan su propio número (1 a 6) para respetar el ciclo operativo; el
 * resto va después, ordenado por nombre.
 */
const NON_SHIPPING_LEGEND_ORDER = 7;

/** Color de la leyenda cuando el evento no trae metadatos (regla `.event-name`). */
const DEFAULT_LEGEND_COLOR = '#11caf0';

export default {
  name: "Calendar",
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    eventDateField: {
      type: String,
      default: "date",
    },
    modelName: {
      type: String,
      default: "shipping",
    },
    /**
     * Pinta en verde el texto de los embarques que el cliente ya pagó (ticket
     * #424). Lo pide el dashboard solo en Duntower.
     */
    markCollectedShippings: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentDate: new Date(),
      isMobile: false,
      selectedDate: null, // Día seleccionado (YYYY-MM-DD)
    };
  },
  computed: {
    /**
     * Eventos agrupados por día civil (YYYY-MM-DD).
     *
     * El día sale de `toYyyyMmDdSync`, que lee la fecha guardada tal cual está
     * (`"2026-11-16"`) en vez de pasarla por `new Date(...)`: esas fechas se parsean
     * como medianoche UTC y en Argentina caían un día antes, así que el evento se
     * dibujaba en la celda equivocada.
     *
     * Además se arma una sola vez por render: antes cada celda recorría la lista
     * completa de eventos, y ahora que el dashboard trae los eventos reales del mes
     * (y no 10 registros) eso eran decenas de miles de comparaciones por pintada.
     */
    eventsByDayKey() {
      const map = {};
      for (const event of this.events || []) {
        if (!event) continue;
        const key = toYyyyMmDdSync(event.eventDate);
        if (!key) continue;
        if (!map[key]) map[key] = [];
        map[key].push(event);
      }
      return map;
    },
    weekDays() {
      return [
        this.$t("calendar.weekDays.sunday"),
        this.$t("calendar.weekDays.monday"),
        this.$t("calendar.weekDays.tuesday"),
        this.$t("calendar.weekDays.wednesday"),
        this.$t("calendar.weekDays.thursday"),
        this.$t("calendar.weekDays.friday"),
        this.$t("calendar.weekDays.saturday"),
      ];
    },
    currentMonthName() {
      return this.currentDate.toLocaleString(
        this.$i18n.locale === "es" ? "es-ES" : "en-US",
        { month: "long" }
      );
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    /** Mes que se está mostrando, como YYYY-MM. */
    visibleMonthKey() {
      return `${this.currentYear}-${String(this.currentDate.getMonth() + 1).padStart(2, "0")}`;
    },
    /**
     * Colores presentes en el mes visible, con cuántos hay de cada uno.
     *
     * Solo entran los que efectivamente se ven: el color de un embarque es su
     * estado operativo actual, así que el mismo campo de fecha trae operaciones de
     * varios colores y cuáles aparecen cambia de un mes a otro. Una leyenda fija de
     * los seis estados mostraría colores que no están en pantalla.
     */
    monthLegend() {
      const monthKey = this.visibleMonthKey;
      const entries = new Map();

      for (const event of this.events || []) {
        if (!event) continue;
        const dayKey = toYyyyMmDdSync(event.eventDate);
        if (!dayKey || dayKey.slice(0, 7) !== monthKey) continue;

        const entry = this.resolveLegendEntry(event);
        if (!entry) continue;

        const existing = entries.get(entry.key);
        if (existing) {
          existing.count += 1;
        } else {
          entries.set(entry.key, { ...entry, count: 1 });
        }
      }

      // Los estados van en su orden operativo; lo demás (pagos, cobros) después y
      // alfabético, para que la leyenda no cambie de orden entre meses.
      return Array.from(entries.values()).sort(
        (a, b) => a.order - b.order || a.label.localeCompare(b.label)
      );
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      // Primer día del mes
      const firstDay = new Date(year, month, 1);
      // Último día del mes
      const lastDay = new Date(year, month + 1, 0);

      // Días del mes anterior para completar la primera semana
      const firstDayWeekday = firstDay.getDay();
      const prevMonthDays = Array.from({ length: firstDayWeekday }, (_, i) =>
        this.buildCalendarDay(new Date(year, month, -i), false)
      ).reverse();

      // Días del mes actual
      const currentMonthDays = Array.from(
        { length: lastDay.getDate() },
        (_, i) => this.buildCalendarDay(new Date(year, month, i + 1), true)
      );

      // Días del mes siguiente para completar la última semana
      const lastDayWeekday = lastDay.getDay();
      const nextMonthDays = Array.from({ length: 6 - lastDayWeekday }, (_, i) =>
        this.buildCalendarDay(new Date(year, month + 1, i + 1), false)
      );

      // Combinar todos los días
      const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

      // Dividir en semanas
      const weeks = [];
      for (let i = 0; i < allDays.length; i += 7) {
        weeks.push(allDays.slice(i, i + 7));
      }

      return weeks;
    },
    mobileCalendarDays() {
      // Para móvil, mostrar solo los días del mes actual con eventos
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      // Días del mes actual
      const lastDay = new Date(year, month + 1, 0);
      const currentMonthDays = Array.from(
        { length: lastDay.getDate() },
        (_, i) => this.buildCalendarDay(new Date(year, month, i + 1), true)
      );

      // Filtrar solo días que tienen eventos o son hoy
      return currentMonthDays.filter((day) => {
        return this.hasEventOnDate(day.dayKey) || day.isToday;
      });
    },
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    getDayName(date) {
      const dayNames = [
        this.$t("calendar.weekDays.sunday"),
        this.$t("calendar.weekDays.monday"),
        this.$t("calendar.weekDays.tuesday"),
        this.$t("calendar.weekDays.wednesday"),
        this.$t("calendar.weekDays.thursday"),
        this.$t("calendar.weekDays.friday"),
        this.$t("calendar.weekDays.saturday"),
      ];
      return dayNames[new Date(date).getDay()];
    },
    /** Celda de la grilla. `dayKey` es el día civil, y con eso se cruzan los eventos. */
    buildCalendarDay(date, currentMonth) {
      return {
        date: date.toISOString(),
        dayKey: toLocalYyyyMmDd(date),
        dayNumber: date.getDate(),
        currentMonth,
        isToday: this.isToday(date),
      };
    },
    hasEventOnDate(dayKey) {
      return this.getEventsForDate(dayKey).length > 0;
    },
    isSelectedDate(dayKey) {
      if (!this.selectedDate) return false;
      return this.selectedDate === dayKey;
    },
    getEventsForDate(dayKey) {
      if (!dayKey) return [];
      return this.eventsByDayKey[dayKey] || [];
    },
    isToday(date) {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    },
    previousMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1
      );
      // Limpiar selección al cambiar de mes
      this.selectedDate = null;
      this.$emit("month-changed", this.currentDate);
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1
      );
      // Limpiar selección al cambiar de mes
      this.selectedDate = null;
      this.$emit("month-changed", this.currentDate);
    },
    onDayClick(day) {
      // Marcar el día como seleccionado
      this.selectedDate = day.dayKey;

      // Abrir el panel lateral siempre (también sin eventos: permite crear uno nuevo)
      this.$emit("day-clicked", {
        date: new Date(day.date),
        events: this.getEventsForDate(day.dayKey),
        highlightedEvent: null,
      });
    },
    /**
     * Teléfono: el panel con los eventos del día lo muestra el dashboard arriba del
     * calendario; si la agenda está scrolleada, se sube para que se vea.
     */
    scrollPageTopOnMobile() {
      [document.body, document.scrollingElement].forEach((el) => {
        if (el && el.scrollTo) el.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    onMobileDayClick(day) {
      this.onDayClick(day);
      this.scrollPageTopOnMobile();
    },
    onMobileEventClick(event, day) {
      this.onEventClick(event, day);
      this.scrollPageTopOnMobile();
    },
    /** Escritorio: el chip lleva al ítem; Cmd/Ctrl+click lo abre en otra pestaña. */
    openEventItem(event, domEvent) {
      const to = {
        name: "DynamicModelDetail",
        params: { modelName: event.modelName, itemId: event._id },
      };
      if (domEvent && (domEvent.metaKey || domEvent.ctrlKey)) {
        window.open(this.$router.resolve(to).href, "_blank");
        return;
      }
      this.$router.push(to);
    },
    onEventClick(event, day) {
      // Marcar el día como seleccionado
      this.selectedDate = day.dayKey;

      // Abrir el listado con el evento específico destacado
      this.$emit("day-clicked", {
        date: new Date(day.date),
        events: this.getEventsForDate(day.dayKey),
        highlightedEvent: event._id, // Evento específico a destacar
      });
    },
    /**
     * Color del chip. Los embarques se pintan con su status operativo, el mismo que
     * muestra el badge del listado/reporte (ticket #365: en el dashboard el ETA se
     * veía celeste y en el reporte rojo).
     *
     * Payment y collect se reconocen por `modelName`, no por el texto del label:
     * antes se buscaba «payment»/«collect» dentro del nombre del campo, así que
     * dependía de la traducción y de la abreviatura que tuviera cargada el tenant.
     */
    getEventStatusClass(event) {
      const modelName = event && event.modelName ? String(event.modelName).toLowerCase() : "";
      if (modelName === "shipping") {
        // La clase la sigue necesitando el modo oscuro, que tiene su propia paleta
        // en `assets/css/dark.css` (`#darkdesign .status-*`, con `!important`).
        if (!event.item) return "";
        const statusClass = getShippingStatusClass(event.item);
        if (this.markCollectedShippings && isShippingClientPendingSettled(event.item)) {
          return `${statusClass} ${SHIPPING_COLLECTED_CLASS}`;
        }
        return statusClass;
      }
      // El resto se pinta por estilo inline desde sus metadatos (ver
      // `getEventChipStyle`): el color va por campo, no por modelo.
      return "";
    },
    /**
     * Color de fondo del chip para un embarque: su status operativo, con la misma
     * paleta que el badge del listado/reporte.
     *
     * Va inline porque `.event-name` define su propio color de fondo y, al ser un
     * estilo scoped, le gana en especificidad a la clase `.status-*` global. El
     * estilo inline resuelve eso sin sumar otro `!important`, y en modo oscuro el
     * `!important` de `#darkdesign .status-*` sigue teniendo prioridad sobre él, así
     * que esa paleta se respeta.
     *
     * Los valores salen de `STATUS_COLORS` en vez de estar repetidos en el CSS de
     * este componente. `getShippingStatusColors` usa el `status` que ya calculó el
     * backend y solo lo calcula si no vino: antes, un embarque sin `status` se
     * quedaba con el celeste por defecto.
     */
    /**
     * Teléfono: el color del evento va como franja a la izquierda de una tarjeta clara
     * (texto legible sobre el fondo del sistema); en escritorio sigue el chip de color.
     */
    getMobileEventStyle(event) {
      const chip = this.getEventChipStyle(event);
      return chip.backgroundColor ? { borderLeftColor: chip.backgroundColor } : {};
    },
    getEventChipStyle(event) {
      if (!event) return {};
      const modelName = String(event.modelName || "").toLowerCase();

      if (modelName === "shipping" && event.item) {
        const colors = getShippingStatusColors(event.item);
        return { backgroundColor: colors.background, color: colors.text };
      }

      // Pagos, cobros y demás: el color sale de los metadatos del campo, así que
      // la solicitud de pago y el pago efectivo no comparten color.
      const info = event.eventTypeInfo;
      if (info && info.color) {
        return { backgroundColor: info.color, color: info.textColor || "#000000" };
      }
      return {};
    },
    /** Nombre visible de la operación (o del documento) al que pertenece el evento. */
    getEventOperationText(event) {
      if (!event) return "";
      const item = event.item || {};
      return event.title || item.title || item.name || "";
    },
    /**
     * Entrada de leyenda que le corresponde a un evento: mismo color que su chip.
     * Es la contracara de `getEventChipStyle` / `getEventStatusClass`.
     */
    resolveLegendEntry(event) {
      const modelName = String(event.modelName || "").toLowerCase();

      if (modelName === "shipping" && event.item) {
        const status = getShippingStatus(event.item);
        return {
          key: `status-${status}`,
          color: getStatusColors(status).background,
          label: this.$t(getStatusLabel(status, this.$t)),
          order: status,
        };
      }

      // Una entrada por campo, no por modelo: «Payment Request» y «Payment» son
      // eventos distintos y tienen que contarse y pintarse por separado.
      //
      // El texto es el nombre del campo tal como lo muestra el engranaje («Payment
      // date», «Collection date»), no la etiqueta corta del tipo («Payment»,
      // «Collection»): ticket #424, el usuario no reconocía a qué fecha se refería.
      const info = event.eventTypeInfo;
      return {
        key: `${modelName}.${event.fieldKey}`,
        color: (info && info.color) || DEFAULT_LEGEND_COLOR,
        label: this.$t(event.field || (info && info.typeKey) || event.modelName || ""),
        order: NON_SHIPPING_LEGEND_ORDER,
      };
    },
    /**
     * Texto completo del evento para el `title` del chip: en la celda el nombre se
     * corta con ellipsis y así se puede leer sin abrir el día.
     */
    getEventTooltip(event) {
      if (!event) return "";
      const parts = [];
      const field = event.field ? this.$t(event.field) : "";
      const title = event.title || (event.item && (event.item.title || event.item.name)) || "";
      if (field) parts.push(field);
      if (title) parts.push(title);
      let text = parts.join(": ");
      if (String(event.modelName || "").toLowerCase() === "shipping" && event.item) {
        text += ` — ${this.$t(getShippingStatusLabel(event.item, this.$t))}`;
        if (this.markCollectedShippings && isShippingClientPendingSettled(event.item)) {
          text += ` — ${this.$t("Fully collected")}`;
        }
      }
      return text;
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
  },
};
</script>

<style scoped>
.calendar-container {
  width: 100%;
}

/* Leyenda de colores: una sola línea a lo ancho, entre el mes y la grilla. */
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  font-size: 0.8125rem;
  line-height: 1.2;
}

.calendar-legend-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.calendar-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.calendar-legend-count {
  margin-left: 6px;
  font-weight: 600;
  opacity: 0.65;
}

/* Navegación del mes: botones livianos, el título se lleva el peso. */
.calendar-nav-btn {
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
  background: var(--bs-body-bg);
  color: var(--bs-primary);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.calendar-nav-btn:hover {
  background: rgba(var(--bs-primary-rgb), 0.08);
  border-color: rgba(var(--bs-primary-rgb), 0.35);
  color: var(--bs-primary);
}

.calendar-title {
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: -0.01em;
}

/*
 * La grilla es una sola tarjeta: las líneas entre días salen del `gap` sobre el
 * fondo del contenedor, así no se suman dos bordes por celda.
 */
.desktop-calendar {
  border: 1px solid #e6e8ec;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04);
  background-color: #eef0f3;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  border-bottom: 1px solid #e6e8ec;
}

.calendar-day-header {
  padding: 10px;
  text-align: center;
  font-weight: 600;
  font-size: 0.8rem;
  color: #5b6472;
  background-color: #f8f9fb;
}

.calendar-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.calendar-day {
  min-height: 100px;
  padding: 6px;
  position: relative;
  background-color: #fff;
  transition: background-color 0.15s ease;
}

.calendar-day:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.03);
}

.other-month {
  background-color: #f8f9fb;
  color: #9aa1ad;
}

.has-event {
  overflow: hidden;
}

.day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 4px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.day-number:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.12);
}

.today .day-number:hover {
  background-color: var(--bs-primary);
  box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.25);
}

.other-month .day-number {
  font-weight: 500;
}

/* Hoy: el número en un círculo del primario y un tinte suave en la celda. */
.today {
  background-color: rgba(var(--bs-primary-rgb), 0.05);
}

.today .day-number {
  background-color: var(--bs-primary);
  color: #fff;
}

.calendar-day.selected {
  box-shadow: inset 0 0 0 2px var(--bs-primary);
}

.event-content {
  margin-top: 4px;
  /* Un punto menos que antes (0.8em): la celda es angosta y cada carácter cuenta. */
  font-size: 0.75em;
}

.event-name {
  background-color: #11caf0;
  padding: 3px 6px;
  border-radius: 5px;
  margin-bottom: 3px;
  color: #000000;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.08);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  /*
   * El código del campo queda fijo y el nombre de la operación se lleva el resto
   * del ancho: si algo tiene que cortarse es el nombre, nunca el código, para que
   * no se confunda un ETA con un Gate Out.
   */
  display: flex;
  align-items: baseline;
  gap: 4px;
  overflow: hidden;
}

.event-name:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(16, 24, 40, 0.15);
}

.event-operation {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-field-code {
  flex: 0 0 auto;
  font-weight: 600;
  opacity: 0.7;
  font-size: 0.9em;
  letter-spacing: 0.02em;
}


/*
 * Los chips de pago y cobro ya no tienen clase propia: su color sale de los
 * metadatos del campo (`dashboardEventTypes.js`) por estilo inline, porque una
 * clase por modelo pintaba igual la solicitud de pago y el pago efectivo.
 */

/* Estilos para vista móvil */
.mobile-calendar {
  width: 100%;
}

.mobile-events-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-day-card {
  background: var(--m-surface);
  border: 1px solid var(--m-border);
  border-radius: 8px;
  padding: 16px;
  box-shadow: var(--bs-box-shadow-sm);
  transition: all 0.2s ease;
  cursor: pointer;
}

.mobile-day-card:hover {
  box-shadow: var(--bs-box-shadow);
  transform: translateY(-1px);
}

.mobile-day-card.today {
  border-color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.06);
}

.mobile-day-card.other-month {
  opacity: 0.6;
}

.mobile-day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.mobile-day-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mobile-day-number {
  font-size: 24px;
  font-weight: bold;
  color: var(--m-text);
  min-width: 30px;
}

.mobile-day-name {
  font-size: 16px;
  color: var(--m-muted);
  font-weight: 500;
}

.mobile-event-count {
  background-color: var(--bs-primary);
  color: var(--bs-white);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.mobile-events {
  margin-top: 8px;
}

.mobile-event-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  background-color: var(--m-surface);
  border: 1px solid var(--m-border);
  border-left: 4px solid var(--m-border);
  transition: all 0.2s ease;
}

.mobile-event-item:hover {
  background-color: var(--m-surface-2);
}

.mobile-event-item:last-child {
  margin-bottom: 0;
}

.mobile-event-content {
  flex: 1;
  min-width: 0;
}

.mobile-event-field {
  font-size: 12px;
  color: var(--m-muted);
  margin-bottom: 2px;
  font-weight: 500;
}

.mobile-event-name {
  font-size: 14px;
  color: var(--m-text);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-event-arrow {
  color: var(--m-muted);
  margin-left: 8px;
}

.mobile-event-arrow i {
  font-size: 18px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .calendar-container {
    padding: 0 10px;
  }

  .desktop-calendar {
    display: none;
  }

  .mobile-calendar {
    display: block;
  }
}

@media (min-width: 769px) {
  .calendar-container {
    width: 100%;
  }

  .desktop-calendar {
    display: block;
  }

  .mobile-calendar {
    display: none;
  }
}

/*
 * Los colores de status de los embarques ya no se definen acá.
 *
 * Este archivo tenía un segundo bloque `<style>` SIN `scoped` con `.status-*` y
 * `!important`. Al no estar scoped se inyectaba en toda la app: los badges de los
 * listados y reportes quedaban pisados por esta paleta una vez que el usuario
 * entraba al dashboard (lo que se reportó como «los status de colores no se ven
 * actualizados»), y encima varios componentes dependían sin saberlo de esa fuga
 * para tener color.
 *
 * La definición global vive ahora en `assets/scss/md/_shipping-status.scss` y los
 * chips de este calendario se pintan por estilo inline desde
 * `utils/shippingStatus.js -> STATUS_COLORS`, que es la única fuente de los colores.
 */
</style>